// Libraries
import { Modal, Col, List, DatePicker, Select, Space, Tooltip } from 'antd';
import moment from 'moment';
import { useState, useEffect, useMemo } from 'react';

// Components
import ButtonComponent from '../../subComponents/ButtonComponent';
import SelectComponent from '../../subComponents/SelectComponent';
import { ContainerShipmentType } from '../../../types/containerShipmentTypes';

// Store
import { useAppDispatch } from '../../../store/store';
import { getAllContainersInProcess, getAllContainerShipmentsByDownloadDate, updateContainerShipment, setDownloadShimpmentDate } from '../../../store/redux/actions/containerShipmentActions';
import { useSelector } from 'react-redux';
import { EditOutlined, FileAddOutlined, MinusCircleOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import * as containerShipmentSelectors from '../../../store/redux/selectors/containerShipmentSelectors';
import * as resourcesSelectors from '../../../store/redux/selectors/resourcesSelectors';

// Constants
import { UPDATE_CONTAINER_SHIPMENT } from '../../../utils/constants';

type PropsType = {
  onToggleModal: (visible: boolean) => void;
  recordsDate: Date;
};

const formatDateTime = (value: moment.Moment | Date | string | null) => {
  if (!value) return '-';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
};

const PlanningFormModal = (props: PropsType) => {
  const { onToggleModal, recordsDate } = props;
  const dispatch = useAppDispatch();
  const { shipmentStateParams } = useSelector(containerShipmentSelectors.containerShipmentParamsSelector);
  const allContainersInProcess = useSelector(containerShipmentSelectors.allContainersInProcess);
  const containerShipments = useSelector(containerShipmentSelectors.containerShipmentByDownloadDateSelector);
  const resources = useSelector(resourcesSelectors.resourcesSelector);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<ContainerShipmentType>>({});
  const [list, setList] = useState<ContainerShipmentType[]>([]);
  const [totalCartons, setTotalCartons] = useState<number>(0);
  const [totalSkus, setTotalSkus] = useState<number>(0);
  const [totalResources, setTotalResources] = useState<number>(0);
  const [totalDifference, setTotalDifference] = useState<number>(0);
  const [selectedContainerId, setSelectedContainerId] = useState<string>();

  useEffect(() => {
    dispatch(getAllContainerShipmentsByDownloadDate(recordsDate));
    dispatch(getAllContainersInProcess());
  }, []);

  useEffect(() => {
    const totalCartons = list?.reduce((sum, item) => sum + (item.totalCartons || 0), 0);
    const totalSkus = list?.reduce((sum, item) => sum + (item.skuQuantity || 0), 0);
    const totalResources = (resources?.workers ?? 0) * (resources?.hours ?? 0) * (resources?.productivity ?? 0);
    const totalDifference =  totalResources - totalCartons;

    setTotalCartons(totalCartons);
    setTotalSkus(totalSkus);
    setTotalResources(totalResources);
    setTotalDifference(totalDifference);
  }, [list, resources]);

  useEffect(() => {
    const newList: ContainerShipmentType[] = !containerShipments ? [] : containerShipments.flatMap((containerShipment) => [
      {
        ...containerShipment,
        statusName: shipmentStateParams.find((status) => status.id.toString() == containerShipment.status.toString())?.value?.toString(),
      }
    ]);

    setList(newList);
  }, [shipmentStateParams, containerShipments]);

  const availableContainers = useMemo(() => {
    const assignedShipmentIds = new Set(list.map(containerShipment => containerShipment.shipmentId));

    return allContainersInProcess.filter(containerShipment => !assignedShipmentIds.has(containerShipment.shipmentId));
  }, [allContainersInProcess, list]);

  const handleEdit = (record: ContainerShipmentType) => {
    setEditingKey(record.shipmentId);
    setEditData({ ...record });
  };

  const handleSave = async () => {
    if (editData.shipmentId) {
      dispatch(updateContainerShipment(editData as ContainerShipmentType, UPDATE_CONTAINER_SHIPMENT.schedule, recordsDate));
      setEditingKey(null);
      setEditData({});
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditData({});
  };

  const handleAddContainer = () => {
    if (!selectedContainerId) {
      return;
    }

    dispatch(setDownloadShimpmentDate(selectedContainerId, recordsDate));
    setSelectedContainerId(undefined);
  };

  const handleRemoveContainer = () => {
    if (!editData.shipmentId) {
      return;
    }

    dispatch(setDownloadShimpmentDate(editData.shipmentId, null, recordsDate));
    setSelectedContainerId(undefined);
  };

  const isEditing = (shipmentId: string) => shipmentId === editingKey;

  return (
    <Modal open={true} maskClosable closable={false} keyboard={false} destroyOnClose={true} width={1000}
      title={
        <Col className={'grid grid-cols-1 md:grid-cols-2 gap-2 items-center'}>
          <span>Planeación: (Semana: {moment(recordsDate).format('W')} Día: {formatDateTime(recordsDate)})</span>
          <Space className='justify-end'>
            <SelectComponent allowClear={true} showSearch={true} searchFieldName='children' value={selectedContainerId}
                            placeHolder='Seleccionar contenedor' className='w-56'
                            list={availableContainers.map(containerShipment => ({
                              id: containerShipment.shipmentId,
                              value: containerShipment.containerNumber,
                            }))}
                            onChange={(value) => setSelectedContainerId(value ? value.toString() : undefined)}
            />
            <ButtonComponent text={'Agregar contenedor'} icon={<FileAddOutlined />} className='h-8 px-2' disabled={!selectedContainerId}
                            onClick={handleAddContainer}
            />
          </Space>
        </Col>
      }
      footer={[
        <ButtonComponent
          key={'cancel'}
          text={'Cerrar'}
          className={'w-32 h-8 bg-white border border-grey-dkt-300 leading-4 text-grey-dkt-700 ' +
            'hover:bg-grey-dkt-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-dkt-400'}
          onClick={() => onToggleModal(false)}
        />,
      ]}
    >
      <Col className='space-y-4'>
        <List
          dataSource={list}
          locale={{ emptyText: 'No hay registros para este día.' }}
          header={
            <Col className='grid grid-cols-9 gap-2 text-xs font-semibold text-grey-dkt-700 border-b border-grey-dkt-300 pb-2'>
              <span>Contenedor</span>
              <span>Tamaño</span>
              <span>ATA</span>
              <span>Estado</span>
              <span>Fecha límite</span>
              <span>Programado</span>
              <span>SKUs</span>
              <span>Cajas</span>
              <span>Acción</span>
            </Col>
          }
          footer={
            <Col className='space-y-2'>
              <Col className='grid grid-cols-9 gap-2 text-xs font-semibol text-grey-dkt-700 border-t border-grey-dkt-300 pt-2'>
                <span>Total</span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span className='truncate'>{totalSkus}</span>
                <span className='truncate'>{totalCartons}</span>
                <span></span>
              </Col>
              <Col className='grid grid-cols-9 gap-2 text-xs font-semibold border-grey-dkt-300 pt-2'>
                <span>Recursos</span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span className='truncate'>{totalResources}</span>
                <span></span>
              </Col>
              <Col className='grid grid-cols-9 gap-2 text-xs font-semibold text-grey-dkt-700 border-t border-grey-dkt-300 pt-2'>
                <span>Diferencia</span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span className='truncate'>{totalDifference}</span>
                <span></span>
              </Col>
            </Col>
          }
          renderItem={(item) => (
            <List.Item key={item.shipmentId} className={`px-0 py-3 ${!item.isOnTime ? 'bg-red-dkt-100 rounded-lg' : ''}`}>
              <Col className='grid grid-cols-9 gap-2 text-xs w-full items-center'>
                <span className='truncate'>{item.containerNumber || '-'}</span>
                <span className='truncate'>{item.containerSize || '-'}</span>
                <span className='truncate'>{formatDateTime(item.ata)}</span>
                
                {isEditing(item.shipmentId) ? (
                  <Select
                    size='small'
                    value={editData.status?.toString()}
                    onChange={(value) => setEditData({ ...editData, status: parseInt(value)})}
                    placeholder='Estado'
                    optionLabelProp='children'
                    className='w-full'>
                    {shipmentStateParams?.map((state) => (
                      <Select.Option key={state.id} value={state.id}>
                        {state.value}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <span className='truncate'>{item.statusName || '-'}</span>
                )}

                <span className='truncate'>{formatDateTime(item.unloadingDeadline)}</span>

                {isEditing(item.shipmentId) ? (
                  <DatePicker
                    size='small'
                    value={editData.scheduledUnloadingDate ? moment(editData.scheduledUnloadingDate) : null}
                    onChange={(date) => setEditData({ ...editData, scheduledUnloadingDate: date?.toDate() || null })}
                    format='DD/MM/YYYY'
                    className='w-full'
                  />
                ) : (
                  <span className='truncate'>{formatDateTime(item.scheduledUnloadingDate)}</span>
                )}
  
                <span className='truncate'>{item.skuQuantity ?? '-'}</span>
                <span className='truncate'>{item.totalCartons ?? '-'}</span>

                <Col className='flex gap-1'>
                  {isEditing(item.shipmentId) ? (
                    <>
                      <Space>
                        <Tooltip title={'Guardar'}>
                          <SaveOutlined className='text-lg cursor-pointer hover:text-blue-dkt-400'
                            onClick={() => handleSave()}
                          />
                        </Tooltip>
                        <Tooltip title={'Remover'}>
                          <MinusCircleOutlined
                            className='text-lg cursor-pointer hover:text-blue-dkt-400'
                            onClick={() => handleRemoveContainer()}
                          />
                        </Tooltip>
                        <Tooltip title={'Cancelar'}>
                          <StopOutlined
                            className='text-lg cursor-pointer hover:text-blue-dkt-400'
                            onClick={handleCancel}
                          /> 
                        </Tooltip>
                      </Space>
                    </>
                  ) : (
                    <Space>
                      <Tooltip title={'Modificar'}>
                        <EditOutlined className='text-lg cursor-pointer hover:text-blue-dkt-400'
                          onClick={() => handleEdit(item)}
                        />
                      </Tooltip>
                    </Space>
                  )}
                </Col>
              </Col>
            </List.Item>
          )}
        />
      </Col>
    </Modal>
  );
}

export default PlanningFormModal;

